/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Thursday, July 7th 2022, 9:37:04 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import ListItemText from "@mui/material/ListItemText";
import { useTranslatedText } from "core";
import {
  APIMany2OneSelectFormInput,
  ConcreteAPIMany2OneSelectFormInputProps,
  APIMany2ManyTransferListFormInput,
} from "ui-widgets";
import { ProcessDefinition, ProcessStage } from "lib/schemas";
import { APISERVICEPATHS } from "lib/api";
import WorkspacesIcon from "@mui/icons-material/Workspaces";
import { PrimaryKeyValue } from "lib/types";
import { useServiceBuilderContext } from "service-builder/providers/ServiceBuilderContext";

type APITaskTemplateFilters = {
  process_definition?: number & PrimaryKeyValue<ProcessDefinition>;
} & Partial<ProcessStage>;

interface ProcessStageInputProps<PrimaryObject>
  extends ConcreteAPIMany2OneSelectFormInputProps<PrimaryObject, ProcessStage> {
  apiQueryParams?: APITaskTemplateFilters;
}

export const ProcessStageInput = <PrimaryObject,>(props: ProcessStageInputProps<PrimaryObject>) => {
  const {
    processDefinitionContext: { currentProcessDefinition },
  } = useServiceBuilderContext();

  const LANG = useTranslatedText();

  return (
    <APIMany2OneSelectFormInput<PrimaryObject, ProcessStage>
      label={props.label ?? "Choose a Stage"}
      itemIdProperty="id"
      getOptionLabel={(option) => option.stage_name}
      loadingText="Loading Stages for this service workflow"
      apiServicePath={APISERVICEPATHS.processStages}
      apiQueryParams={{ process_definition: currentProcessDefinition.id, active: true }}
      inputStartAdornment={<WorkspacesIcon color="primary" />}
      helperText={<>Choose a stage for this task. {LANG.stageSettingsDescription}</>}
      noOptionsText={
        <>No Active Stages. Please add stages for this workflow by clicking on the 'Workflow Stages' button.</>
      }
      {...props}
    />
  );
};

export const ProcessStagesTransferListInput = <PrimaryObject,>(props: ProcessStageInputProps<PrimaryObject>) => {
  const {
    processDefinitionContext: { currentProcessDefinition },
  } = useServiceBuilderContext();

  return (
    <APIMany2ManyTransferListFormInput<PrimaryObject, ProcessStage>
      itemIdProperty="id"
      getOptionLabel={(option) => <ListItemText primary={`${option.stage_name}`} />}
      apiServicePath={APISERVICEPATHS.processStages}
      apiQueryParams={{ process_definition: currentProcessDefinition.id, active: true }}
      {...props}
    />
  );
};
